import { useEffect, useState } from 'react'
import { customerData } from '../../../utilities/CustomerData'
import { apiURL } from '../../../utilities/URLs'
import BlueInkEmbed from '@blueink360/blueink-embed-js'
import { refreshAccessToken } from '../../../utilities/extensions'
import { useNavigate } from 'react-router-dom'

export function AdvancePaymentESign({ packetId, applicationQuestionnaire }) {
  const navigate = useNavigate()
  const [eSignStep, setESignStep] = useState(0)
  const [embedUrl, setEmbedUrl] = useState('')
  const [signedDoc, setSignedDoc] = useState(false)
  const [uploadingDoc, setUploadingDoc] = useState(false)

  const embed = new BlueInkEmbed(process.env.REACT_APP_BLUEINK_PUBLIC_API_KEY)

  embed.on(BlueInkEmbed.EVENT.COMPLETE, () => {
    setSignedDoc(true)
  })

  useEffect(() => {
    const handleGetEmbedURL = async () => {
      const form = new FormData()
      form.append('embedInfo', JSON.stringify({
        leadContact: customerData.customerLeadData.leadContact,
        advanceFundingApplication: customerData.customerAdvanceFunding,
        advanceFundingQuestionnaire: applicationQuestionnaire
      }))

      const embedUrlResponse = await fetch(`${apiURL}/api/getEmbedURL`, {
        method: 'POST',
        headers: {
          'Authorization': customerData.accessToken
        },
        body: form
      })
      const embedUrlData = await embedUrlResponse.json()
      if(embedUrlData.response.error) {
        if(embedUrlData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetEmbedURL() : navigate('/', { state: { expired: true } })
        } else if(embedUrlData.response.error === 'No IDme Attributes.')
          navigate('/resyncIDme') 
          else 
          console.error(embedUrlData.response.error)
      } else
        setEmbedUrl(embedUrlData.response.embedUrl)
    }

    const handleGetPacketEmbedURL = async () => {
      const embedUrlResponse = await fetch(`${apiURL}/api/getExistingEmbedURL?packetId=${encodeURIComponent(packetId)}`, {
        headers: {
          'Authorization': customerData.accessToken
        }
      })
      const embedUrlData = await embedUrlResponse.json()
      if(embedUrlData.response.error) {
        if(embedUrlData.response.error === 'Failed To Authenticate Token.') {
          const refreshedToken = await refreshAccessToken()
          refreshedToken && refreshedToken.length > 0 ? await handleGetPacketEmbedURL() : navigate('/', { state: { expired: true } })
        } else
          console.error(embedUrlData.response.error)
      } else
        setEmbedUrl(embedUrlData.response.embedUrl)
    }

    if(packetId)
      handleGetPacketEmbedURL()
    else
      handleGetEmbedURL()
  }, [])

  useEffect(() => {
    if (eSignStep === 1 && embedUrl)
      embed.mount(embedUrl, '#iframe-container', { height: '1000px' })
  }, [eSignStep, embedUrl])

  const handleESignComplete = async () => {
    setUploadingDoc(true)

    const eSignCompleteResponse = await fetch(`${apiURL}/api/completeESign?leadId=${encodeURIComponent(customerData.customerLeadData.leadId)}`, {
      headers: {
        'Authorization': customerData.accessToken
      }
    })
    const eSignCompleteData = await eSignCompleteResponse.json()
    setUploadingDoc(false)
    if(eSignCompleteData.response.error) {
      if(eSignCompleteData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleESignComplete() : navigate('/', { state: { expired: true } })
      } else
        console.error(eSignCompleteData.response.error)
    } else {
      setESignStep(eSignStep+1)
      customerData.updateCustomerStage('adf32a79-07cf-42c3-8052-e0068ff538eb', '63956276-b103-4f49-b63f-15ae0a28f1fa', 'Documents Available - DocSigned')
    }
  }

  return (
    <div className={`flex flex-col items-center lg:p-10 ${eSignStep === 0 || eSignStep === 2 ? 'lg:max-w-screen-lg w-11/12' : 'lg:w-screen w-11/12'}`}>
      {eSignStep === 0 ? (
        <div className='w-full flex-flex-col items-center'>
          <p className='text-2xl font-bold mb-4'>E-Sign</p>
          <div className='w-full flex flex-col gap-4'>
            <p>Before we send off your amended tax return to the IRS, you need to electronically sign agreements with SETC Pros Inc. (as your tax preparer) and Community Bank and Trust of West Georgia (the bank offering your Refund Anticipation Loan). Click the button below to get started e-signing.</p>
            <button onClick={() => setESignStep(eSignStep + 1)} className='w-fit rounded-lg p-3 bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white self-center'>
              Get Started
            </button>
          </div>
        </div>
      ) : eSignStep === 1 ? (
        <div className='w-full flex flex-col items-center justify-center gap-4'>
          <p className='text-2xl font-bold'>Express Refund Signature</p>
          {embedUrl ? (
            <div className='w-full h-full flex flex-1 flex-col items-center gap-4'>
              <div className='lg:w-1/2 w-11/12 h-[70vh]' id='iframe-container'></div>
              <button disabled={!signedDoc || uploadingDoc} onClick={handleESignComplete} className={`rounded-lg p-3 text-white self-center ${signedDoc && !uploadingDoc ? 'bg-primaryColor hover:bg-primaryDarkColor transition duration-200' : 'bg-gray-400 cursor-not-allowed'}`}>
                Complete E-Sign
              </button>
              {uploadingDoc && (
                <div className='flex flex-row items-center justify-center gap-2'>
                  <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] h-8 w-8 mt-4 mb-4'></div>
                  <p>Completing E-Sign...</p>
                </div>
              )}
            </div>
          ) : (
            <div className='flex flex-row items-center justify-center gap-2'>
              <div className='loader ease-linear rounded-full border-[6px] border-t-[6px]  h-8 w-8 mt-4 mb-4'></div>
              <p>Loading...</p>
            </div>
          )}
        </div>
      ) : (
        <div className='w-full flex-flex-col items-center'>
          <p className='text-2xl font-bold mb-4'>E-Sign Complete</p>
          <div className='w-full flex flex-col gap-4'>
            <p>Your E-Signing is complete! We are currently processing our e-signatures.</p>
            <button onClick={() => navigate('/dashboard')} className='w-fit rounded-lg p-3 bg-primaryColor hover:bg-primaryDarkColor transition duration-200 text-white self-center'>
              Return to Dashboard
            </button>
          </div>
        </div>
      )}
    </div>
  )
}